import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ManualInvoice,
  CustomInvoiceFieldValue,
  ManualInvoicePartialPaySetting,
  PaymentPreview,
} from 'src/app/invoice/models/invoice.model';
import { DatePipe } from '@angular/common';
import { Settings } from 'src/app/shared/models/settings/settings.model';
import { InvoiceTemplateModel } from 'src/app/invoice/models/invoice-template.model';
import { AuthService } from 'src/app/core/auth.service';
import { InvoiceService } from 'src/app/invoice/services/invoice-service.service';
import {
  Customer,
  CustomerAddress,
} from 'src/app/shared/models/customer.model';
import { LocationModel } from 'src/app/settings/models/location/location-details.model';
import { TermsAndConditionModel } from 'src/app/settings/models/terms-conditions-policy-model';
import { InvoiceSummaryComponent } from 'src/app/invoice/invoice-summary/invoice-summary.component';

@Component({
  selector: 'app-print-preview',
  templateUrl: './print-preview.component.html',
  styleUrls: ['./print-preview.component.scss'],
})
export class PrintPreviewComponent implements OnInit {
  @Input() invoiceType: string;
  @Input() invoice: any;
  @Input() invoicePolicies: TermsAndConditionModel[] = [];
  @Input() businessInfo: Settings = new Settings();
  @Input() customer: Customer = new Customer();
  @Input() customerAddress: string;
  @Input() customerAddresses: CustomerAddress[];
  @Input() customerSignature: any;
  @Input() locationData: LocationModel;
  @Input() paymentData: PaymentPreview = new PaymentPreview();
  @Input() invoicePayments: any;
  @Input() workOrder: any;
  @Input() partialPayment: ManualInvoicePartialPaySetting;
  @Input() customFieldValues: CustomInvoiceFieldValue[];
  @Input() isSummary: boolean = true;

  isLoading: boolean = false;
  invoiceData: ManualInvoice;
  invoiceTemplate: any = new InvoiceTemplateModel();
  assigndEmployees: any[] = [];
  workOrderData: any[] = [];
  customerSig: any;

  constructor(
    public datepipe: DatePipe,
    private authService: AuthService,
    private invoiceService: InvoiceService,
    private modalRef: NgbActiveModal,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.isLoading = true;

    this.businessInfo.Logo = this.locationData.Logo
      ? `data:image/png;base64,${this.locationData.Logo}`
      : this.businessInfo.Logo;
    if (this.locationData.IsLocationNameDisplayed) {
      this.businessInfo.BusinessName = this.locationData.BranchName;
    }

    if (this.invoiceType !== 'estimate') {
      this.remapInvoice();
      this.invoiceService.getActiveInvoiceTemplate().subscribe(
        (result: any) => {
          if (result) {
            this.invoiceTemplate = result;
          }
        },
        (error) => {
          console.log(error);
          this.isLoading = false;
        },
        () => {
          setTimeout(() => {
            this.isLoading = false;
          }, 800);
        }
      );
      return;
    }

    setTimeout(() => {
      this.isLoading = false;
    }, 800);
  }

  onClose(data?: any) {
    this.modalRef.close(data);
  }

  onReturnInvoiceDetails(): void {
    let invoiceDiag = this.modalService.open(InvoiceSummaryComponent, {
      size: 'lg',
      keyboard: false,
      backdrop: 'static',
    });

    invoiceDiag.componentInstance.invoiceType = this.invoiceType;
    invoiceDiag.componentInstance.invoiceNumber =
      this.invoice[
        this.invoiceType !== 'estimate' ? 'invoiceNumber' : 'estimateNumber'
      ];
    this.onClose();
  }

  remapInvoice(): void {
    const commercialLineItems = this.invoice.commercialData?.commercialLineItems || [];

    this.invoiceData = {
      InvoiceId: this.invoice.id,
      InvoiceNumber: this.invoice.invoiceNumber,
      InvoiceDate: new Date(this.invoice.invoiceDate),
      DueDate: this.datepipe.transform(this.invoice.dueDate, 'MM/dd/yyyy'),
      AmountDue: this.invoice.amountDue,
      TotalAmount: this.invoice.totalAmount,
      PartialDue: this.invoice.partialDue,
      CustomerId: this.invoice.customerId,
      LocationName: this.invoice.locationName,
      PartialDueType: this.invoice.partialDueType,
      DiscountType: this.invoice.discountType === 'USD' ? '$' : '%',
      TaxType: this.invoice.taxType === 'USD' ? '$' : '%',
      UpchargeType: this.invoice.upchargeType === 'USD' ? '$' : '%',
      Balance: this.invoice.balance,
      Discount: this.invoice.discount,
      Tax: this.invoice.tax,
      Upcharge: this.invoice.upcharge,
      PaymentTerm: this.invoice.paymentTerm,
      PaymentTermValue: this.invoice.paymentTermValue,
      Status: this.invoice.status,
      Notes: this.invoice.notes,
      TCPId: this.invoice.tcpId,
      TCPIds: this.invoice.tcpIds,
      TaxableTotal: this.invoice.taxableTotal,
      NonTaxableTotal: this.invoice.nonTaxableTotal,
      CreatedByUserName: this.invoice.createdByUserName,
      CreatedDateTZBased: this.invoice.createdDateTZBased,
      UpdatedByUserName: this.invoice.updatedByUserName,
      UpdatedDateTZBased: this.invoice.updatedDateTZBased,
      InvoiceItems: this.invoice.invoiceItems.map((item, index) => {
        return {
          Id: item.id,
          ItemId: item.itemId,
          ItemType: item.itemType,
          ItemName: item.itemName,
          Quantity: item.quantity,
          IsTaxExempt: item.isTaxExempt,
          Rate: item.rate,
          Amount: item.amount,
          ServiceDescription: item.serviceDescription,
          ProductCode: commercialLineItems[index]?.productCode || null,
          ItemCommodityCode: commercialLineItems[index]?.itemCommodityCode || null,
          UnitOfMeasure: commercialLineItems[index]?.unitOfMeasure || null,
          DiscountAmount:
            commercialLineItems[index]?.discountAmount
              ? `$${commercialLineItems[index]?.discountAmount}`
              : commercialLineItems[index]?.discountRate
                ? `${commercialLineItems[index]?.discountRate}%`
                : null,
          TaxAmount:
            commercialLineItems[index]?.taxAmount
              ? `$${commercialLineItems[index]?.taxAmount}`
              : commercialLineItems[index]?.taxRate
                ? `${commercialLineItems[index]?.taxRate}%`
                : null,
          TaxType:
            commercialLineItems[index]?.additionalTaxType === 'SALESTAX'
              ? 'Sales Tax'
              : commercialLineItems[index]?.additionalTaxType === 'TAXEXEMPT'
                ? 'Tax Exempt'
                : commercialLineItems[index]?.additionalTaxType === 'NOTUSED'
                  ? 'Not Used'
                  : null,
          TaxTreatment:
            commercialLineItems[index]?.taxTreatment === '1'
              ? 'BEFORE Discount'
              : commercialLineItems[index]?.taxTreatment === '2'
                ? 'AFTER Discount'
                : null,
        };
      }),
    };
  }

  getTagsHtml(tagName: keyof HTMLElementTagNameMap): string {
    const htmlStr: string[] = [];
    const elements = document.getElementsByTagName(tagName);

    for (let idx = 0; idx < elements.length; idx++) {
      if (
        tagName === 'link' &&
        elements[idx].outerHTML.includes('href="styles')
      ) {
        elements[idx].outerHTML = elements[idx].outerHTML.replace(
          'href="styles',
          'href="/styles'
        );
      }

      htmlStr.push(elements[idx].outerHTML);
    }

    return htmlStr.join('\r\n');
  }

  getDefaultCustomerAddress(): string {
    if (this.customerAddresses) {
      const address = this.customerAddresses.find((e) => e.IsDefault);

      if (address) {
        return `${address.StreetAddress || ''} ${address.City || ''} ${
          address.State || ''
        } ${address.ZipCode || ''}`;
      }

      return '';
    }
  }

  getGrossTotal(): number {
    if (this.invoice) {
      if (this.invoice.partialDueType === '%') {
        return (
          this.invoice.totalAmount -
          Math.fround(
            (this.invoice.totalAmount * this.invoice.partialDue) / 100
          )
        );
      }

      return this.invoice.totalAmount - this.invoice.partialDue;
    }
  }

  getTipAmount(): number {
    if (this.invoice) {
      if (this.invoice.tipType === '%') {
        return Math.fround((this.invoice.totalAmount * this.invoice.tip) / 100);
      }

      return this.invoice.tip;
    }
  }

  printInvoice() {
    let printContents, popupWin;

    const stylesHtml = this.getTagsHtml('style');
    const linksHtml = this.getTagsHtml('link');
    const type =
      this.invoiceType === 'estimate'
        ? 'Estimate'
        : this.invoiceType === 'manual'
        ? 'Manual Invoice'
        : 'Recurring Invoice';

    printContents = document.getElementById('print-section').innerHTML;


    popupWin = window.open('', '',
      'toolbar=no,location=no,directories=no,menubar=yes,scrollbars=yes,resizable=yes,width=1000,height=900,left=100,top=25'
    );
    popupWin.document.write(`
      <html>
        <head>
          ${stylesHtml}
          ${linksHtml}
          <title>${type} ${
      this.invoice[
        this.invoiceType === 'estimate' ? 'estimateNumber' : 'invoiceNumber'
      ]
    }</title>
          <style>
            body {
              color: #203645;
              font-family: Inter, sans-serif;
              font-size: 1rem;
              font-weight: 400;
            }
            .form-group {
              width: 100%;
            }
            .hide-on-print{
              display:none;
            }
            .dv-empty {
              text-align: center;
              width: 100%;
              min-height: 100vh;
            }
            .dv-empty img {
              height: 50px;
              margin: 40px 0;
            }
            .dv-empty h1 {
              font-size: 32px;
              margin-bottom: 20px;
            }
          </style>
        </head>
      <body onload="window.print();">${printContents}</body>
      </html>`);
    popupWin.document.close();
    // popupWin.document.focus();
  }

  canAccessTipModule() {
    let nav = this.authService.user.SideNavAccess.find(
      (e) => e.SideNavName == 'Tips'
    );
    return nav ? nav.IsEnabled : true;
  }
}
